import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import AdminLayout from "@/components/admin/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Eye, CheckCircle, Phone, Mail, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const statusOptions = [
  { value: "all", label: "Tous" },
  { value: "pending", label: "En attente" },
  { value: "contacted", label: "Contacté" },
  { value: "completed", label: "Traité" },
];

const AdminDevis = () => {
  const [devis, setDevis] = useState<any[]>([]);
  const [filtered, setFiltered] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedDevis, setSelectedDevis] = useState<any>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchDevis();
  }, []);

  useEffect(() => {
    let result = devis;
    if (statusFilter !== "all") {
      result = result.filter((d) => d.status === statusFilter);
    }
    if (search) {
      const s = search.toLowerCase();
      result = result.filter(
        (d) =>
          d.full_name?.toLowerCase().includes(s) ||
          d.email?.toLowerCase().includes(s) ||
          d.company_name?.toLowerCase().includes(s)
      );
    }
    setFiltered(result);
  }, [devis, search, statusFilter]);

  const fetchDevis = async () => {
    const { data } = await supabase
      .from("quote_requests")
      .select("*")
      .order("created_at", { ascending: false });
    if (data) {
      setDevis(data);
      setFiltered(data);
    }
  };

  const updateStatus = async (id: string, status: string) => {
    const { error } = await supabase
      .from("quote_requests")
      .update({ status })
      .eq("id", id);

    if (error) {
      toast({ title: "Erreur", description: error.message, variant: "destructive" });
      return;
    }

    toast({ title: "Statut mis à jour" });
    fetchDevis();
    if (selectedDevis?.id === id) {
      setSelectedDevis({ ...selectedDevis, status });
    }
  };

  const deleteDevis = async (id: string) => {
    if (!confirm("Supprimer cette demande de devis ?")) return;
    const { error } = await supabase.from("quote_requests").delete().eq("id", id);
    if (error) {
      toast({ title: "Erreur", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Demande supprimée" });
    setSelectedDevis(null);
    fetchDevis();
  };

  const formatProducts = (products: any) => {
    if (!products || !Array.isArray(products)) return "—";
    return products.map((p: any) => `${p.name || p.id} (x${p.quantity || 1})`).join(", ");
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      pending: "bg-amber-100 text-amber-700",
      contacted: "bg-blue-100 text-blue-700",
      completed: "bg-emerald-100 text-emerald-700",
    };
    const labels: Record<string, string> = {
      pending: "En attente",
      contacted: "Contacté",
      completed: "Traité",
    };
    return (
      <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Filters */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher par nom, email, entreprise..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="flex gap-2">
                {statusOptions.map((opt) => (
                  <Button
                    key={opt.value}
                    variant={statusFilter === opt.value ? "default" : "outline"}
                    size="sm"
                    onClick={() => setStatusFilter(opt.value)}
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results count */}
        <p className="text-sm text-muted-foreground">{filtered.length} demande(s) trouvée(s)</p>

        {/* Table */}
        <Card className="border-0 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary/50">
                <tr>
                  <th className="text-left p-4 font-medium text-muted-foreground">Date</th>
                  <th className="text-left p-4 font-medium text-muted-foreground">Nom</th>
                  <th className="text-left p-4 font-medium text-muted-foreground hidden md:table-cell">Email</th>
                  <th className="text-left p-4 font-medium text-muted-foreground hidden lg:table-cell">Entreprise</th>
                  <th className="text-left p-4 font-medium text-muted-foreground">Statut</th>
                  <th className="text-left p-4 font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((d) => (
                  <tr key={d.id} className="border-t border-border hover:bg-secondary/30 transition-colors">
                    <td className="p-4">{new Date(d.created_at).toLocaleDateString("fr-FR")}</td>
                    <td className="p-4 font-medium">{d.full_name}</td>
                    <td className="p-4 hidden md:table-cell text-muted-foreground">{d.email}</td>
                    <td className="p-4 hidden lg:table-cell text-muted-foreground">{d.company_name || "—"}</td>
                    <td className="p-4">{getStatusBadge(d.status)}</td>
                    <td className="p-4">
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={() => setSelectedDevis(d)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        {d.status === "pending" && (
                          <Button variant="ghost" size="sm" onClick={() => updateStatus(d.id, "contacted")}>
                            <Phone className="w-4 h-4" />
                          </Button>
                        )}
                        {d.status === "contacted" && (
                          <Button variant="ghost" size="sm" onClick={() => updateStatus(d.id, "completed")}>
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Detail modal */}
        <Dialog open={!!selectedDevis} onOpenChange={() => setSelectedDevis(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Détails du devis</DialogTitle>
            </DialogHeader>
            {selectedDevis && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Nom</p>
                    <p className="font-medium">{selectedDevis.full_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Entreprise</p>
                    <p className="font-medium">{selectedDevis.company_name || "—"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Email</p>
                    <a href={`mailto:${selectedDevis.email}`} className="text-primary font-medium flex items-center gap-1">
                      <Mail className="w-3 h-3" /> {selectedDevis.email}
                    </a>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Téléphone</p>
                    {selectedDevis.phone ? (
                      <a href={`tel:${selectedDevis.phone}`} className="text-primary font-medium flex items-center gap-1">
                        <Phone className="w-3 h-3" /> {selectedDevis.phone}
                      </a>
                    ) : (
                      <p>—</p>
                    )}
                  </div>
                </div>

                <div>
                  <p className="text-xs text-muted-foreground">Date</p>
                  <p>{new Date(selectedDevis.created_at).toLocaleString("fr-FR")}</p>
                </div>

                <div>
                  <p className="text-xs text-muted-foreground">Statut</p>
                  <div className="flex gap-2 mt-1">
                    {getStatusBadge(selectedDevis.status)}
                  </div>
                </div>

                <div>
                  <p className="text-xs text-muted-foreground mb-1">Produits demandés</p>
                  <div className="bg-secondary/50 rounded-lg p-3 text-sm">
                    {formatProducts(selectedDevis.products)}
                  </div>
                </div>

                {selectedDevis.message && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Message</p>
                    <div className="bg-secondary/50 rounded-lg p-3 text-sm whitespace-pre-wrap">
                      {selectedDevis.message}
                    </div>
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  {selectedDevis.status === "pending" && (
                    <Button size="sm" onClick={() => updateStatus(selectedDevis.id, "contacted")}>
                      Marquer comme contacté
                    </Button>
                  )}
                  {selectedDevis.status === "contacted" && (
                    <Button size="sm" onClick={() => updateStatus(selectedDevis.id, "completed")}>
                      Marquer comme traité
                    </Button>
                  )}
                  <Button size="sm" variant="destructive" onClick={() => deleteDevis(selectedDevis.id)}>
                    Supprimer
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminDevis;
